import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { getInviteInfo, acceptInvite } from "../lib/api";
import { saveSession } from "../lib/auth";

export default function AcceptInvite() {
  const navigate       = useNavigate();
  const [params]       = useSearchParams();
  const token          = params.get("token");

  const [invite,      setInvite]      = useState(null);
  const [loadErr,     setLoadErr]     = useState("");
  const [loading,     setLoading]     = useState(true);

  const [fullName,    setFullName]    = useState("");
  const [password,    setPassword]    = useState("");
  const [confirm,     setConfirm]     = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [submitErr,   setSubmitErr]   = useState("");

  useEffect(() => {
    if (!token) { setLoadErr("No invitation token found."); setLoading(false); return; }
    getInviteInfo(token)
      .then(res => { setInvite(res); setLoading(false); })
      .catch(e  => { setLoadErr(e.message || "Invalid or expired invitation."); setLoading(false); });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setSubmitErr("Passwords do not match."); return; }
    if (password.length < 8)  { setSubmitErr("Password must be at least 8 characters."); return; }
    setSubmitting(true);
    setSubmitErr("");
    try {
      const res = await acceptInvite(token, fullName, password);
      saveSession(res.token, res.user);
      navigate("/dashboard");
    } catch (err) {
      setSubmitErr(err.message || "Failed to accept invitation.");
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <div style={styles.logoMark}>M</div>
          <span style={styles.logoText}>MasterFileAI</span>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <div style={styles.spinner} />
            <p style={{ color: "#6b7280", marginTop: 12, fontSize: 14 }}>Checking invitation…</p>
          </div>
        )}

        {!loading && loadErr && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠</div>
            <h2 style={{ color: "#111827", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Invitation not found</h2>
            <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 24 }}>{loadErr}</p>
            <Link to="/login" style={{ color: "#6c63ff", fontSize: 14 }}>Go to login →</Link>
          </div>
        )}

        {!loading && invite && (
          <>
            <div style={styles.welcomeBox}>
              <div style={styles.welcomeTitle}>You're invited to join</div>
              <div style={styles.companyName}>{invite.company_name}</div>
              <div style={styles.inviteMeta}>
                Joining as <strong style={{ textTransform: "capitalize" }}>{invite.role}</strong> · {invite.email}
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
              <div style={styles.field}>
                <label style={styles.label}>Your full name</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="First and last name"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  required
                  disabled={submitting}
                  autoFocus
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Create a password</label>
                <input
                  style={styles.input}
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  disabled={submitting}
                  autoComplete="new-password"
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Confirm password</label>
                <input
                  style={styles.input}
                  type="password"
                  placeholder="Repeat password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  disabled={submitting}
                  autoComplete="new-password"
                />
              </div>

              {submitErr && <p style={styles.errMsg}>{submitErr}</p>}

              <button type="submit" style={styles.btn} disabled={submitting || !fullName.trim() || !password || !confirm}>
                {submitting ? "Setting up account…" : "Accept invitation →"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page:        { minHeight: "100vh", background: "#f8f9fb", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "'Inter', system-ui, sans-serif" },
  card:        { background: "#fff", border: "1px solid #e2e6ef", borderRadius: 16, padding: "40px 40px", width: "100%", maxWidth: 420 },
  logoRow:     { display: "flex", alignItems: "center", gap: 10, marginBottom: 28 },
  logoMark:    { width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg, #6c63ff, #3ecfcf)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 16, flexShrink: 0 },
  logoText:    { color: "#111827", fontWeight: 700, fontSize: 15, letterSpacing: "-0.3px" },
  welcomeBox:  { background: "rgba(108,99,255,0.05)", border: "1px solid rgba(108,99,255,0.2)", borderRadius: 10, padding: "18px 20px", textAlign: "center" },
  welcomeTitle:{ color: "#6b7280", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 },
  companyName: { color: "#111827", fontSize: 20, fontWeight: 700, letterSpacing: "-0.3px", marginBottom: 6 },
  inviteMeta:  { color: "#6b7280", fontSize: 13 },
  field:       { marginBottom: 16 },
  label:       { display: "block", color: "#374151", fontSize: 13, fontWeight: 500, marginBottom: 6 },
  input:       { width: "100%", padding: "10px 13px", borderRadius: 8, border: "1px solid #e2e6ef", fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box" },
  errMsg:      { color: "#ef4444", fontSize: 13, marginBottom: 12 },
  btn:         { width: "100%", background: "linear-gradient(135deg, #6c63ff, #3ecfcf)", border: "none", borderRadius: 10, color: "#111827", cursor: "pointer", fontSize: 15, fontWeight: 600, padding: "13px", marginTop: 4 },
  spinner:     { width: 28, height: 28, border: "3px solid #e9ecf3", borderTop: "3px solid #6c63ff", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" },
};
